import { ApiRequestError, guest } from '@croissants/contracts';
import type { Locale } from '@croissants/i18n';
import { createPortalClient } from '@croissants/ui';
import { API_BASE_URL } from './config';

/**
 * The typed client. One zod schema per endpoint is both the server's validation
 * and this app's inferred type (tech-stack section 2), so a screen cannot read a
 * field the API does not send.
 *
 * Auth is the signed QR session token in `x-guest-session` - there is no identity
 * provider on this mount and no cookie, which is what keeps the journey under
 * three minutes (tech-stack section 7).
 */

const SESSION_TOKEN_STORAGE_KEY = 'cib.session';
const SESSION_EXPIRY_STORAGE_KEY = 'cib.session.expires';

function storedCredential(): { token: string; expiresAt: string } | null {
  try {
    const token = globalThis.sessionStorage?.getItem(SESSION_TOKEN_STORAGE_KEY) ?? null;
    const expiresAt = globalThis.sessionStorage?.getItem(SESSION_EXPIRY_STORAGE_KEY) ?? null;
    if (token === null || expiresAt === null || Date.parse(expiresAt) <= Date.now()) return null;
    return { token, expiresAt };
  } catch {
    return null;
  }
}

const initialCredential = storedCredential();
let currentSessionToken: string | null = initialCredential?.token ?? null;
let currentSessionExpiry: string | null = initialCredential?.expiresAt ?? null;
let sessionIdentityGeneration = 0;
let currentLocale: Locale = 'en';
const renewalListeners = new Set<(credential: { token: string; expiresAt: string }) => void>();

/** Keeps only the furthest expiry when concurrent requests finish out of order. */
export function adoptSessionCredential(
  token: string,
  expiresAt: string,
  expectedGeneration: number = sessionIdentityGeneration,
): void {
  if (expectedGeneration !== sessionIdentityGeneration) return;
  const next = Date.parse(expiresAt);
  const current =
    currentSessionExpiry === null ? Number.NEGATIVE_INFINITY : Date.parse(currentSessionExpiry);
  if (!Number.isFinite(next) || next < current) return;
  currentSessionToken = token;
  currentSessionExpiry = expiresAt;
  try {
    globalThis.sessionStorage?.setItem(SESSION_TOKEN_STORAGE_KEY, token);
    globalThis.sessionStorage?.setItem(SESSION_EXPIRY_STORAGE_KEY, expiresAt);
  } catch {
    // Private browsing may reject storage; the in-memory session still works.
  }
  for (const listener of renewalListeners) listener({ token, expiresAt });
}

export function clearSessionCredential(): void {
  sessionIdentityGeneration += 1;
  currentSessionToken = null;
  currentSessionExpiry = null;
  try {
    globalThis.sessionStorage?.removeItem(SESSION_TOKEN_STORAGE_KEY);
    globalThis.sessionStorage?.removeItem(SESSION_EXPIRY_STORAGE_KEY);
  } catch {
    // Storage is optional.
  }
}

/** Captured beside a request so a response from a previous QR cannot take over. */
export function sessionCredentialGeneration(): number {
  return sessionIdentityGeneration;
}

export function onSessionRenewed(
  listener: (credential: { token: string; expiresAt: string }) => void,
): () => void {
  renewalListeners.add(listener);
  return () => renewalListeners.delete(listener);
}

export function setSessionToken(token: string | null): void {
  // A renewal may have completed between render and this assignment. Its
  // listener updates React Query immediately; keep the newer credential until
  // that render arrives rather than briefly restoring the response it replaced.
  if (token !== null && currentSessionExpiry !== null && currentSessionToken !== token) return;
  currentSessionToken = token;
}

export function setRequestLocale(locale: Locale): void {
  currentLocale = locale;
}

export const api = createPortalClient({
  baseUrl: API_BASE_URL,
  // A header session, never a cookie: see `pilotCookie` (@croissants/ui, F6).
  pilotCookie: false,
  fetchImpl: async (input, init) => {
    const generation = sessionIdentityGeneration;
    const response = await globalThis.fetch(input, init);
    const token = response.headers.get(guest.GUEST_SESSION_RENEWED_HEADER);
    const expiresAt = response.headers.get(guest.GUEST_SESSION_EXPIRES_HEADER);
    if (token !== null && expiresAt !== null) {
      adoptSessionCredential(token, expiresAt, generation);
    }
    return response;
  },
  headers: () => {
    const headers: Record<string, string> = { 'accept-language': currentLocale };
    if (currentSessionToken !== null) {
      headers[guest.GUEST_SESSION_HEADER] = currentSessionToken;
    }
    return headers;
  },
});

/**
 * True when the server refused the CHOSEN SHOP rather than the request: the
 * guest must choose again (`error.choose_shop`, 409 - the refusal F-10
 * standardized). The mode B shop choice is persisted per printed code, so a
 * stored id the world no longer offers - a house deactivated, suspended or
 * re-tuned out of range since the guest's last visit - arrives here on the
 * picker and the vitrine. The screens answer it by clearing the stored choice
 * and reopening the chooser; without that, the guest was stranded on an empty
 * "no windows" morning with no error and no way back.
 */
export function isVoidShopChoice(error: unknown): boolean {
  return (
    error instanceof ApiRequestError &&
    error.status === 409 &&
    error.messageKey === 'error.choose_shop'
  );
}

/**
 * True when the shop serving a MODE B2 visit has stopped trading
 * (`error.shop_changed`, 409 - founder decision 2026-09-20).
 *
 * The sibling of the refusal above and deliberately a different key, because
 * the recovery is different. There is no chooser in B2 and nothing the guest
 * picked, so "choose again" is not an instruction they can act on: the shop was
 * bound to this session at the scan, and only a scan may change it. The app
 * re-resolves the session it already holds, which re-computes the closest shop,
 * and empties a basket that was filled from a different house's shelf.
 */
export function isShopChanged(error: unknown): boolean {
  return (
    error instanceof ApiRequestError &&
    error.status === 409 &&
    error.messageKey === 'error.shop_changed'
  );
}

/**
 * What actually went wrong, in the only two shapes a guest screen can act on.
 *
 * `REJECTED` - the server answered, and its answer was about the request: this
 * code is unknown, forged, revoked or expired. Sending it again changes nothing.
 * `UNREACHABLE` - we never got a usable answer: the connection dropped, the
 * origin refused it, the worker fell over. The request is worth repeating and
 * the code is very probably fine.
 *
 * The distinction is what stops a guest being told the wrong thing. Both
 * failures used to arrive at the same place - a rejected query - so whichever
 * screen that place happened to map to was shown for both, and one of the two
 * was always a lie: either "this code did not open" over a dropped connection,
 * or "check your connection" over a code the server had already refused twice.
 */
export type RequestFailureKind = 'REJECTED' | 'UNREACHABLE';

export function requestFailureKind(error: unknown): RequestFailureKind | null {
  if (error === null || error === undefined) return null;
  if (!(error instanceof ApiRequestError)) return 'UNREACHABLE';
  // status 0 is this client's marker for "the fetch itself threw".
  if (error.code === 'network_error' || error.status === 0) return 'UNREACHABLE';
  // The server is broken, not the code; and 429 is explicitly "come back".
  if (error.status >= 500 || error.status === 429) return 'UNREACHABLE';
  // A response that did not match the contract says nothing about the code.
  if (error.code === 'invalid_response') return 'UNREACHABLE';
  return 'REJECTED';
}

/**
 * Whether repeating this exact request could ever produce a different answer.
 * Used as the retry predicate, so a definitive refusal settles on the first
 * response instead of being asked twice and answered the same way twice.
 */
export function isRetriable(error: unknown): boolean {
  return requestFailureKind(error) === 'UNREACHABLE';
}
